import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { Col, Input, Row } from 'reactstrap';
import Datepicker from 'components/Inputs/DatePicker';
import FlexSelect from 'components/Inputs/FlexSelect';
import CPFMask from 'components/Inputs/CPFMask';
import { FlexController } from 'components/FlexControllerInput';

import { customFieldTypes } from 'views/fieldConfigs/utils/customFieldTypes';
import CurrencyMask from 'components/Inputs/CurrencyMask';
import { useCustomFields } from 'views/Protocol/form/context/CustomFieldsContext';
import CNPJMask from 'components/Inputs/CNPJMask';
const TextInput = ({ field, control }) => (
  <>
    <label htmlFor={field.name}>{field.label}</label>
    <Controller
      render={(props) => (
        <Input
          value={props.value}
          onChange={props.onChange}
          type="text"
          placeholder={field.label}
        />
      )}
      control={control}
      defaultValue=""
      name={field.name}
      id={field.name}
    />
  </>
);

const TextAreaInput = ({ field, control }) => (
  <>
    <label htmlFor={field.name}>{field.label}</label>
    <Controller
      render={(props) => (
        <Input
          value={props.value}
          onChange={props.onChange}
          type="textarea"
          placeholder={field.label}
          style={{ minHeight: '7.5rem', resize: 'vertical' }}
        />
      )}
      control={control}
      defaultValue=""
      name={field.name}
      id={field.name}
    />
  </>
);

const NumericInput = ({ field, control }) => (
  <>
    <label htmlFor={field.name}>{field.label}</label>
    <Controller
      render={(props) => (
        <Input
          value={props.value}
          onChange={props.onChange}
          type="number"
          placeholder={field.label}
        />
      )}
      control={control}
      defaultValue=""
      name={field.name}
      id={field.name}
    />
  </>
);

const CPFInput = ({ field, control, setValue }) => (
  <>
    <label htmlFor={field.name}>{field.label}</label>
    <Controller
      render={(props) => (
        <CPFMask
          valueController={setValue}
          fieldName={field.name}
          value={props.value}
          placeholder="000.000.000-00"
        />
      )}
      control={control}
      defaultValue=""
      name={field.name}
      rules={{
        validate: (value) => {
          if (!value) return true;
          const cpfFormated = value.replace(/\D/g, '');
          if (!cpfFormated) return true;
          if (cpfFormated.length !== 11) return 'CPF inválido';
          return true;
        }
      }}
    />
  </>
);

const CNPJInput = ({ field, control, setValue }) => (
  <>
    <label htmlFor={field.name}>{field.label}</label>
    <Controller
      render={(props) => (
        <CNPJMask
          valueController={setValue}
          fieldName={field.name}
          value={props.value}
          placeholder="00.000.000/0001-00"
        />
      )}
      control={control}
      defaultValue=""
      name={field.name}
      rules={{
        validate: (value) => {
          if (!value) return true;
          const cnpjFormated = value.replace(/\D/g, '');
          if (!cnpjFormated) return true;
          if (cnpjFormated.length !== 14) return 'CNPJ inválido';
          return true;
        }
      }}
    />
  </>
);

const dateRules = {
  pattern: {
    value: /^[0-3][0-9]\/[0-1][0-9]\/[0-9][0-9][0-9][0-9]$/,
    message: 'Data inválida! (dd/MM/aaaa)'
  }
};

const DateInputBegin = ({ field, control, setValue, getValues }) => (
  <>
    <div key={field.id}>
      <label htmlFor={field.name}>{field.label} (Data Inicial)</label>
      <Controller
        render={(props) => (
          <Datepicker
            fieldName={field.name + '.DateBegin'}
            defaultValue={props.value}
            valueController={setValue}
          />
        )}
        control={control}
        defaultValue=""
        name={field.name + '.DateBegin'}
        id={field.name}
        rules={{
          ...dateRules,
          required: getValues(field.name + '.DateEnd')
            ? 'Data é obrigatória'
            : false
        }}
      />
    </div>
  </>
);

const DateInputEnd = ({ field, control, setValue, getValues, toISOFormat }) => (
  <>
    <div key={field.id}>
      <label htmlFor={field.name}>{field.label} (Data Final)</label>
      <Controller
        render={(props) => (
          <Datepicker
            fieldName={field.name + '.DateEnd'}
            defaultValue={props.value}
            valueController={setValue}
          />
        )}
        control={control}
        defaultValue=""
        name={field.name + '.DateEnd'}
        id={field.name}
        rules={{
          ...dateRules,
          required: getValues(field.name + '.DateBegin')
            ? 'Data é obrigatória'
            : false,
          validate: async (value) => {
            if (
              new Date(await toISOFormat(value, true)) <
              new Date(
                await toISOFormat(getValues(field.name + '.DateBegin'), true)
              )
            ) {
              return 'A data final não pode ser anterior à inicial!';
            } else {
              return true;
            }
          }
        }}
      />
    </div>
  </>
);
const MultipleInput = ({ field, control, setValue }) => (
  <>
    <label htmlFor={field?.name}>{field?.label}</label>
    <Controller
      render={(props) => (
        <FlexSelect
          dataOptions={field?.options.map((option) => ({
            label: option?.value,
            value: option?.value
          }))}
          isMulti={true}
          isClearable={true}
          value={props.value}
          valueController={setValue}
          fieldName={field?.name}
          labelName="label"
          valueName="value"
          closeMenuOnSelect={false}
        />
      )}
      control={control}
      defaultValue={[]}
      name={field?.name}
      id={field?.name}
    />
  </>
);

const ListInput = ({
  field,
  setValue,
  getValues,
  control,
  verifyParentId,
  customFields
}) => {
  const getOptions = () => {
    const campoPai = customFields.filter(
      (cf) => cf?.name === field?.parentName
    )[0];

    return (
      campoPai?.options
        .filter((opt) => opt?.value === getValues(campoPai?.name))[0]
        ?.parentsValue?.map((option) => ({
          label: option,
          value: option
        })) || []
    );
  };

  return (
    <>
      <label htmlFor={field.name}>{field.label}</label>
      <Controller
        render={(props) => (
          <FlexSelect
            dataOptions={
              !field?.isReference
                ? field.options.map((option) => ({
                    label: option.value,
                    value: option.value
                  }))
                : getOptions()
            }
            isMulti={false}
            isClearable={true}
            value={props.value}
            valueController={(fieldName, fieldValue) => {
              setValue(fieldName, fieldValue);
              verifyParentId(field, fieldValue);
            }}
            closeMenuOnSelect={true}
            fieldName={field.name}
            labelName="label"
            valueName="value"
          />
        )}
        control={control}
        defaultValue=""
        name={field.name}
        id={field.name}
      />
    </>
  );
};

const CurrencyInput = ({ field, control, setValue }) => (
  <>
    <label htmlFor={field.name}>{field.label}</label>
    <Controller
      render={(props) => (
        <CurrencyMask
          valueController={setValue}
          value={props.value}
          fieldName={field.name}
        />
      )}
      control={control}
      defaultValue=""
      name={field.name}
      id={field.name}
    />
  </>
);

const HourField = ({ field, control }) => (
  <>
    <label htmlFor={field.name}>{field.label}</label>
    <Controller
      render={(props) => (
        <Input
          value={props.value}
          onChange={props.onChange}
          type="time"
          step="2"
          placeholder={field.label}
        />
      )}
      control={control}
      defaultValue=""
      name={field.name}
      id={field.name}
    />
  </>
);

const CustomFieldsFilterForm = ({
  customFields,
  control,
  setValue,
  toISOFormat,
  getValues,
  watch
}) => {
  const { changeFieldsReference, getValueFieldsReference } = useCustomFields();

  useEffect(() => {
    if (!customFields || !customFields.length) return;

    const getValuesData = getValues();

    const filteredFields = customFields.filter(
      (field) =>
        field.type === 'LIST' &&
        (!field.hasOwnProperty.call('isReference') ||
          field.isReference === false) &&
        field.parentName
    );

    for (let i = 0; i < filteredFields.length; i++) {
      const field = filteredFields[i];
      const fieldValue = getValuesData[field.name];

      if (fieldValue) {
        verifyParentId(field, fieldValue);
      }
    }
  }, []);

  if (!customFields || !customFields.length) return null;

  const getSearchInput = (field) => {
    if (field.type === customFieldTypes.TEXTAREA)
      return <TextAreaInput field={field} control={control} />;
    if (field.type === customFieldTypes.TELEPHONE)
      return (
        <FlexController
          type="phone"
          label={field.label}
          name={field.name}
          disabled={false}
          showMessageButton={false}
          showCallButton={false}
          handleSendTemplate={() => {}}
          watch={watch}
          control={control}
          errors={{}}
          rules={{}}
          required={false}
          validateOnSubmit={false}
          valueController={(fieldName, value) => {
            setValue(fieldName, value);
          }}
        />
      );

    if (
      field.type === customFieldTypes.TEXT ||
      field.type === customFieldTypes.LINK
    )
      return <TextInput field={field} control={control} />;

    if (field.type === customFieldTypes.CPF)
      return <CPFInput field={field} control={control} setValue={setValue} />;

    if (field.type === customFieldTypes.CNPJ)
      return <CNPJInput field={field} control={control} setValue={setValue} />;

    if (field.type === customFieldTypes.NUMERIC)
      return <NumericInput field={field} control={control} />;

    if (field.type === customFieldTypes.LIST)
      return (
        <ListInput
          field={field}
          setValue={setValue}
          control={control}
          verifyParentId={verifyParentId}
          customFields={customFields}
          getValues={getValues}
        />
      );

    if (field.type === customFieldTypes.MULTIPLE) {
      return (
        <MultipleInput field={field} setValue={setValue} control={control} />
      );
    }
    if (field.type === customFieldTypes.CURRENCY)
      return (
        <CurrencyInput field={field} setValue={setValue} control={control} />
      );
    if (field.type === customFieldTypes.HOUR)
      return <HourField field={field} control={control} />;
    return null;
  };

  const verifyParentId = (field, value) => {
    if (!field?.isReference && field?.parentName) {
      setValue(String(field.parentName.trim()), value);
      (field?.options ?? []).map((option) => {
        if (option.value === value) {
          changeFieldsReference({
            ...getValueFieldsReference(),
            [field?.parentName]: option.parentsValue.map((value) => ({
              value,
              label: value
            }))
          });
        }
      });

      return;
    }
  };

  return (
    <Row>
      {customFields.map((customField) => {
        if (customField.type === 'DATE' && toISOFormat && getValues) {
          return (
            <>
              <Col xs="12" sm="6" md="6" lg="4" key={customField.id}>
                <DateInputBegin
                  field={customField}
                  control={control}
                  setValue={setValue}
                  toISOFormat={toISOFormat}
                  getValues={getValues}
                />
              </Col>

              <Col
                xs="12"
                sm="6"
                md="6"
                lg="4"
                className="mb-3"
                key={customField.id}
              >
                <DateInputEnd
                  field={customField}
                  control={control}
                  setValue={setValue}
                  toISOFormat={toISOFormat}
                  getValues={getValues}
                />
              </Col>
            </>
          );
        }
        return (
          <Col xs="12" sm="6" md="6" lg="4" key={customField.id}>
            <div className="form-group">{getSearchInput(customField)}</div>
          </Col>
        );
      })}
    </Row>
  );
};

export default CustomFieldsFilterForm;
